import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from 'primereact/button';
import { TabPanel, TabView } from 'primereact/tabview';
import { Toast } from 'primereact/toast';

import { transactionIcon, transactionLabel } from '../../constants';
import useAuthenticatedRequest from '../../utils/TokenRefreshUtils';
import useReceiptPrinter from '../../utils/ReceiptPrint';
import { useIsDesktop } from '../../utils/useMediaQuery';
import { displayDate, displayDateTime, displayTime } from '../../utils/CommonUtils';

import Card from '../common/Card';
import Drawer from '../common/Drawer';
import EmptyState from '../common/EmptyState';
import Money from '../common/Money';
import RecordList from '../common/RecordList';
import SummaryList, { SummaryRow } from '../common/SummaryList';

const PAGE_SIZE = 10;

/**
 * Історія змін і транзакцій.
 *
 * Було: дві таблиці по col-6 і TabView з двома вкладками всередині правої —
 * на телефоні вкладки та їх вміст мали ширину ~160 px.
 * Стало: список змін на всю ширину; деталі відкриваються панеллю на
 * телефоні та займають другу колонку на десктопі.
 */
const SaleHistory = () => {
    const toast = useRef(null);
    const navigate = useNavigate();
    const isDesktop = useIsDesktop();

    const authenticatedRequest = useAuthenticatedRequest();
    const printReceipt = useReceiptPrinter();

    const [shifts, setShifts] = useState([]);
    const [shiftsPage, setShiftsPage] = useState(1);
    const [shiftsTotal, setShiftsTotal] = useState(0);
    const [shiftsLoading, setShiftsLoading] = useState(true);

    const [selectedShiftId, setSelectedShiftId] = useState(null);
    const [shiftDetails, setShiftDetails] = useState(null);
    const [detailsVisible, setDetailsVisible] = useState(false);

    const [transactions, setTransactions] = useState([]);
    const [transactionsPage, setTransactionsPage] = useState(1);
    const [transactionsTotal, setTransactionsTotal] = useState(0);
    const [transactionsLoading, setTransactionsLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const fetchShifts = async () => {
            setShiftsLoading(true);

            const url = `${process.env.REACT_APP_BACKEND_URL}kasa_shifts/get_shifts_history/?page=${shiftsPage}&page_size=${PAGE_SIZE}&ordering=-close_time`;

            try {
                const response = await authenticatedRequest(url, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                });
                const parsed = await response.json();

                if (!cancelled) {
                    setShifts(parsed.results ?? []);
                    setShiftsTotal(parsed.count ?? 0);
                }
            } catch (error) {
                console.error('Не вдалося отримати історію змін', error);
            } finally {
                if (!cancelled) {
                    setShiftsLoading(false);
                }
            }
        };

        fetchShifts();

        return () => {
            cancelled = true;
        };
    }, [authenticatedRequest, shiftsPage]);

    const fetchTransactions = useCallback(
        async (shiftId, page) => {
            setTransactionsLoading(true);

            const url = `${process.env.REACT_APP_BACKEND_URL}kasa_shifts/get_shift_transactions_history/?shift_id=${shiftId}&page=${page}&page_size=${PAGE_SIZE}&ordering=-made_at`;

            try {
                const response = await authenticatedRequest(url, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                });
                const parsed = await response.json();

                setTransactions(parsed.results ?? []);
                setTransactionsTotal(parsed.count ?? 0);
            } catch (error) {
                console.error('Не вдалося отримати транзакції зміни', error);
            } finally {
                setTransactionsLoading(false);
            }
        },
        [authenticatedRequest]
    );

    const selectShift = async (shift) => {
        setSelectedShiftId(shift.id);
        setShiftDetails(null);
        setTransactionsPage(1);

        if (!isDesktop) {
            setDetailsVisible(true);
        }

        const url = `${process.env.REACT_APP_BACKEND_URL}kasa_shifts/get_shift_history_details/${shift.id}/`;
        const response = await authenticatedRequest(url, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        });

        if (response?.status === 200) {
            setShiftDetails(await response.json());
        }

        fetchTransactions(shift.id, 1);
    };

    const openSale = (transaction) => {
        if (transaction.type === 'SALE') {
            navigate(`/salehistory/sale/${transaction.act}`, {
                state: { id: transaction.act },
            });
        }
    };

    const amountsRow = (label, amounts) =>
        amounts?.length ? (
            <SummaryRow label={label}>
                <span className="rc-summary-row__value--stack">
                    {amounts.map((amount) => (
                        <Money
                            key={`${label}-${amount.currency}`}
                            value={amount.sum}
                            currency={amount.currency}
                        />
                    ))}
                </span>
            </SummaryRow>
        ) : null;

    const detailsContent = !selectedShiftId ? (
        <EmptyState
            icon="pi pi-history"
            title="Зміна не вибрана"
            text="Виберіть зміну зі списку, щоб побачити підсумки та транзакції"
        />
    ) : (
        <TabView className="rc-tabs">
            <TabPanel header="Підсумки" leftIcon="pi pi-chart-bar mr-2">
                {shiftDetails ? (
                    <SummaryList>
                        <SummaryRow
                            label="Зміну відкрито"
                            sublabel={shiftDetails.opened_by}
                            value={displayDateTime(shiftDetails.open_time)}
                        />
                        {shiftDetails.close_time && (
                            <SummaryRow
                                label="Зміну закрито"
                                sublabel={shiftDetails.closed_by}
                                value={displayDateTime(shiftDetails.close_time)}
                            />
                        )}
                        {amountsRow('Початковий баланс', shiftDetails.balance_on_open)}
                        {amountsRow('Баланс на кінець', shiftDetails.balance_on_close)}
                        {amountsRow('Продажі', shiftDetails.sales?.amount)}
                        {amountsRow('Внесення', shiftDetails.cash_deposits?.amount)}
                        {amountsRow('Зняття', shiftDetails.cash_withdraws?.amount)}
                        {shiftDetails.revenue?.amount?.length ? (
                            <SummaryRow label="Виручка" total>
                                <span className="rc-summary-row__value--stack">
                                    {shiftDetails.revenue.amount.map((amount) => (
                                        <Money
                                            key={`revenue-${amount.currency}`}
                                            value={amount.sum}
                                            currency={amount.currency}
                                        />
                                    ))}
                                </span>
                            </SummaryRow>
                        ) : null}
                    </SummaryList>
                ) : (
                    <EmptyState icon="pi pi-spin pi-spinner" text="Завантаження підсумків…" />
                )}
            </TabPanel>

            <TabPanel header="Транзакції" leftIcon="pi pi-list mr-2">
                <RecordList
                    items={transactions}
                    loading={transactionsLoading}
                    page={transactionsPage}
                    pageSize={PAGE_SIZE}
                    totalRecords={transactionsTotal}
                    onPageChange={(page) => {
                        setTransactionsPage(page);
                        fetchTransactions(selectedShiftId, page);
                    }}
                    emptyIcon="pi pi-list"
                    emptyTitle="Транзакцій немає"
                    emptyText="За цю зміну не було жодної операції"
                    renderItem={(transaction) => (
                        <li key={`${transaction.type}-${transaction.made_at}-${transaction.act}`}>
                            <div className="rc-list-item" style={{ cursor: 'default' }}>
                                <div
                                    className="rc-list-item__main"
                                    style={{
                                        cursor: transaction.type === 'SALE' ? 'pointer' : 'default',
                                    }}
                                    onClick={() => openSale(transaction)}
                                    onKeyDown={(e) => e.key === 'Enter' && openSale(transaction)}
                                    role={transaction.type === 'SALE' ? 'button' : undefined}
                                    tabIndex={transaction.type === 'SALE' ? 0 : undefined}
                                >
                                    <div className="rc-list-item__title">
                                        <i
                                            className={`${transactionIcon(transaction.type)} mr-2`}
                                        />
                                        {transactionLabel(transaction.type)}
                                        {transaction.type === 'SALE' && ` · акт №${transaction.act}`}
                                    </div>
                                    <div className="rc-list-item__meta">
                                        <i className="pi pi-clock" />
                                        <span>{displayDateTime(transaction.made_at)}</span>
                                        {transaction.made_by && (
                                            <span>· {transaction.made_by}</span>
                                        )}
                                    </div>
                                </div>
                                <div className="rc-list-item__side">
                                    <Money
                                        value={transaction.sum}
                                        currency={transaction.currency}
                                        tone={
                                            transaction.type === 'CASH_WITHDRAW'
                                                ? 'outcome'
                                                : undefined
                                        }
                                    />
                                    {transaction.type === 'SALE' && (
                                        <Button
                                            icon="pi pi-print"
                                            text
                                            rounded
                                            severity="secondary"
                                            aria-label="Друкувати чек"
                                            onClick={() => printReceipt(transaction.act, toast)}
                                        />
                                    )}
                                </div>
                            </div>
                        </li>
                    )}
                />
            </TabPanel>
        </TabView>
    );

    return (
        <>
            <Toast ref={toast} position="top-center" />

            <div className="rc-stack">
                <div>
                    <h1 className="rc-page-title">Історія</h1>
                    <p className="rc-page-subtitle">Закриті та відкриті зміни каси</p>
                </div>

                <div className="rc-split">
                    <div className="rc-split__aside">
                        <Card title="Зміни" flush>
                            <RecordList
                                items={shifts}
                                loading={shiftsLoading}
                                page={shiftsPage}
                                pageSize={PAGE_SIZE}
                                totalRecords={shiftsTotal}
                                onPageChange={setShiftsPage}
                                emptyIcon="pi pi-history"
                                emptyTitle="Історія порожня"
                                emptyText="Ще не було жодної зміни"
                                renderItem={(shift) => (
                                    <li key={shift.id}>
                                        <button
                                            type="button"
                                            className={`rc-list-item ${
                                                selectedShiftId === shift.id
                                                    ? 'rc-list-item--active'
                                                    : ''
                                            }`.trim()}
                                            onClick={() => selectShift(shift)}
                                        >
                                            <div className="rc-list-item__main">
                                                <div className="rc-list-item__title">
                                                    Зміна №{shift.id}
                                                </div>
                                                <div className="rc-list-item__meta">
                                                    <i className="pi pi-clock" />
                                                    <span>
                                                        {displayDate(shift.open_time)},{' '}
                                                        {displayTime(shift.open_time)}
                                                        {shift.close_time &&
                                                            ` — ${displayTime(shift.close_time)}`}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="rc-list-item__side">
                                                <span
                                                    className={`rc-shift-badge rc-shift-badge--on-light ${
                                                        shift.status === 'CLOSED'
                                                            ? 'rc-shift-badge--closed'
                                                            : 'rc-shift-badge--open'
                                                    }`}
                                                >
                                                    <span className="rc-shift-badge__dot" />
                                                    {shift.status === 'CLOSED'
                                                        ? 'Закрита'
                                                        : 'Відкрита'}
                                                </span>
                                            </div>
                                        </button>
                                    </li>
                                )}
                            />
                        </Card>
                    </div>

                    <div className="rc-split__detail--mobile-hidden">
                        <Card
                            title={
                                selectedShiftId ? `Зміна №${selectedShiftId}` : 'Деталі зміни'
                            }
                            flush
                        >
                            {detailsContent}
                        </Card>
                    </div>
                </div>
            </div>

            <Drawer
                visible={detailsVisible && !isDesktop}
                onHide={() => setDetailsVisible(false)}
                title={selectedShiftId ? `Зміна №${selectedShiftId}` : 'Зміна'}
                flush
            >
                {detailsContent}
            </Drawer>
        </>
    );
};

export default SaleHistory;
